import type { AxiosError } from 'axios'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { toast } from 'sonner'
import useSWR from 'swr'

import { Loading } from '@/components/Loading'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { apiFetcher } from '@/data/fetcher'
import { type OAuthConsentError, type OAuthConsentRequest, scopeLabel } from '@/interfaces/OAuth'
import { AuthCard, AuthLayout } from '@/layouts/AuthLayout'
import API from '@/lib/api'
import { useAuth } from '@/lib/auth'

export default function OAuthAuthorizePage() {
  const { t } = useTranslation()
  const { search } = useLocation()
  const { user } = useAuth({ middleware: 'auth', tmp: 'OAuthAuthorize' })

  // Wait for the session, else the API answers 401 before useAuth sends the user to login.
  const { data, error } = useSWR<OAuthConsentRequest, AxiosError<OAuthConsentError>>(
    user ? '/api/v2/oauth/authorize' + search : null,
    apiFetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  )

  const [accountId, setAccountId] = useState<string>()
  const [submitting, setSubmitting] = useState(false)

  const errorRedirect = error?.response?.data?.redirectUri

  useEffect(() => {
    if (errorRedirect) window.location.href = errorRedirect
  }, [errorRedirect])

  const selectedAccountId =
    accountId ??
    data?.accounts.find((account) => account.id === user?.currentAccountId)?.id ??
    data?.accounts[0]?.id

  const respond = (approve: boolean) => {
    setSubmitting(true)

    return API.post<{ redirectUri: string }>('/api/v2/oauth/authorize', {
      query: search.slice(1),
      accountId: selectedAccountId,
      approve,
    })
      .then((res) => {
        window.location.href = res.data.redirectUri
      })
      // A deny ends here too: the API returns access_denied with the client's redirectUri.
      .catch((err: AxiosError<OAuthConsentError>) => {
        const redirectUri = err.response?.data?.redirectUri

        if (redirectUri) {
          window.location.href = redirectUri
          return
        }

        toast.error(t('oauth.consent.submitError'))
        setSubmitting(false)
      })
  }

  if (error && !errorRedirect) {
    return (
      <AuthLayout>
        <title>{t('oauth.consent.error.title')}</title>
        <AuthCard
          title={t('oauth.consent.error.title')}
          // error_description is technical English text from the OAuth library, not for the user.
          description={t('oauth.consent.error.description')}
        >
          {null}
        </AuthCard>
      </AuthLayout>
    )
  }

  if (!data) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <Loading />
      </div>
    )
  }

  const title = t('oauth.consent.title', { client: data.client.name })

  return (
    <AuthLayout>
      <title>{title}</title>

      <AuthCard title={title} description={t('oauth.consent.redirect', { host: new URL(data.redirectUri).host })}>
        <div className="grid gap-5">
          <div className="grid gap-2">
            <p className="text-sm font-medium">{t('oauth.consent.scopes')}</p>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {data.scopes.map((scope) => (
                <li key={scope}>{scopeLabel(t, scope)}</li>
              ))}
            </ul>
          </div>

          {data.accounts.length > 1 && (
            <div className="grid gap-1.5">
              <Label htmlFor="account" className="text-[13px]">{t('oauth.consent.account')}</Label>
              <Select value={selectedAccountId} onValueChange={setAccountId} disabled={submitting}>
                <SelectTrigger id="account" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {data.accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" disabled={submitting} onClick={() => respond(false)}>
              {t('oauth.consent.deny')}
            </Button>
            <Button disabled={submitting || !selectedAccountId} onClick={() => respond(true)}>
              {submitting && <Loader2 className="animate-spin" />}
              {t('oauth.consent.approve')}
            </Button>
          </div>
        </div>
      </AuthCard>
    </AuthLayout>
  )
}
